import { Attachment } from "../../domain/model/Attachment";
import {
  Auth,
  AccessToken,
  Message,
  SignupRequestModel,
  PhoneNumberCodeModel,
  UserIdRequestModel,
} from "../../domain/model/Auth";
import {
  Category,
  CategoryEntity,
  CategoryUnlockCode,
  UnlockCategoryRequestModel,
} from "../../domain/model/Category";
import {
  DiscountCodeCheckRedeemableRequestModel,
  DiscountCodeCheckRedeemableResponseModel,
} from "../../domain/model/DiscountCode";
import {
  Game,
  GameRequestModel,
  UpdateGameRequestModel,
} from "../../domain/model/Game";
import { Package } from "../../domain/model/Package";
import {
  ExecutePaymentRequestModel,
  PaymentMethod,
  PaymentStatus,
  PaymentURLModel,
} from "../../domain/model/Payment";
import { QRCodeResponseModel } from "../../domain/model/QRCode";
import {
  Question,
  Answer,
  SubcategoryNumberOfQuestionsModel,
  GetRandomQuestionRequestModel,
  QuestionGiveRatingRequestModel,
} from "../../domain/model/Question";
import { Transaction } from "../../domain/model/Transaction";
import {
  Player,
  PlayerStatPerCategoryModel,
  SubcategoriesToResetDataModel,
} from "../../domain/model/User";
import { ApiService } from "../api/ApiService";
import { Response } from "../api/Response";
import { API_ENDPOINTS } from "../constant/ApiConstants";
import NetworkApi from "./NetworkApi";

export default class NetworkApiImpl implements NetworkApi {
  apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }
  // auth
  login(auth: Auth): Promise<Response<AccessToken>> {
    return this.apiService.post<AccessToken>(API_ENDPOINTS.LOGIN, auth);
  }
  signup(signupRequestModel: SignupRequestModel): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      API_ENDPOINTS.SIGNUP,
      signupRequestModel
    );
  }
  loginAsGuestPlayer(): Promise<Response<AccessToken>> {
    return this.apiService.post<AccessToken>(API_ENDPOINTS.LOGIN_AS_GUEST, {});
  }
  loginByUID(model: UserIdRequestModel): Promise<Response<AccessToken>> {
    return this.apiService.post<AccessToken>(API_ENDPOINTS.LOGIN_BY_UID, model);
  }
  forgotPassword(email: string): Promise<Response<Message>> {
    return this.apiService.post<Message>(API_ENDPOINTS.FORGOT_PASSWORD, {
      email,
    });
  }
  resetPassword(auth: Auth): Promise<Response<Message>> {
    return this.apiService.post<Message>(API_ENDPOINTS.RESET_PASSWORD, auth);
  }
  sendVerificationCode(
    model: PhoneNumberCodeModel
  ): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      API_ENDPOINTS.TWILIO + API_ENDPOINTS.SEND_VERIFICATION_CODE,
      model
    );
  }
  checkVerificationCode(
    model: PhoneNumberCodeModel
  ): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      API_ENDPOINTS.TWILIO + API_ENDPOINTS.CHECK_VERIFICATION_CODE,
      model
    );
  }

  // file
  uploadFile(fileFormData: FormData): Promise<Response<Attachment>> {
    return this.apiService.post<Attachment>(
      API_ENDPOINTS.UPLOAD_FILE,
      fileFormData
    );
  }

  // players
  getCurrentUserInfo(): Promise<Response<Player>> {
    return this.apiService.get<Player>(API_ENDPOINTS.PLAYERS);
  }
  updatePlayer(player: Player): Promise<Response<Message>> {
    return this.apiService.put<Message>(API_ENDPOINTS.PLAYERS, player);
  }
  addToQuestionsHistory(questionId: string): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      API_ENDPOINTS.PLAYERS + API_ENDPOINTS.QUESTIONS_HISTORY,
      { questionId }
    );
  }
  addToGames(model: GameRequestModel): Promise<Response<Game>> {
    return this.apiService.post<Game>(
      API_ENDPOINTS.PLAYERS + API_ENDPOINTS.GAMES,
      model
    );
  }
  gameStarted(): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      API_ENDPOINTS.PLAYERS + API_ENDPOINTS.GAMES + "/started",
      {}
    );
  }
  resetQuestions(
    model: SubcategoriesToResetDataModel
  ): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      API_ENDPOINTS.PLAYERS + API_ENDPOINTS.QUESTIONS_HISTORY + "/reset",
      model
    );
  }
  addToUnfinishedGames(gameDataJson: string): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      API_ENDPOINTS.PLAYERS + API_ENDPOINTS.UNFINISHED_GAMES,
      { unfinishedGame: gameDataJson }
    );
  }
  removeFromUnfinishedGames(index: number): Promise<Response<Message>> {
    return this.apiService.delete<Message>(
      API_ENDPOINTS.PLAYERS + API_ENDPOINTS.UNFINISHED_GAMES + "/" + index
    );
  }
  deleteAccount(playerId: string): Promise<Response<Message>> {
    return this.apiService.delete<Message>(
      API_ENDPOINTS.PLAYERS + "/" + playerId
    );
  }
  getPlayerCategoriesStats(
    id: string
  ): Promise<Response<Array<PlayerStatPerCategoryModel>>> {
    return this.apiService.get<Array<PlayerStatPerCategoryModel>>(
      `${API_ENDPOINTS.PLAYERS}/${id}${API_ENDPOINTS.STATS}`
    );
  }

  // catgories
  getCategories(
    filterHiddenOnes: boolean
  ): Promise<Response<Array<CategoryEntity>>> {
    var url = API_ENDPOINTS.CATEGORIES;
    if (filterHiddenOnes) {
      url = url + "?filterHiddenOnes=true";
    }
    return this.apiService.get<Array<CategoryEntity>>(url);
  }
  unlockCategory(
    model: UnlockCategoryRequestModel,
    isUnlockedInReference?: boolean
  ): Promise<Response<Array<CategoryUnlockCode>>> {
    var url = API_ENDPOINTS.CATEGORIES + API_ENDPOINTS.UNLOCK;

    if (isUnlockedInReference) {
      url += `?isUnlockedByReference=${true}`;
    }
    return this.apiService.post<Array<CategoryUnlockCode>>(url, model);
  }
  getCategoryUnlockCode(id: string): Promise<Response<CategoryUnlockCode>> {
    return this.apiService.get<CategoryUnlockCode>(
      API_ENDPOINTS.CATEGORY_UNLOCK_CODES + "/" + id
    );
  }

  // questions
  getNumberOfRemainingQuestions(
    categoriesId: Array<string>
  ): Promise<Response<Array<Array<SubcategoryNumberOfQuestionsModel>>>> {
    return this.apiService.get<Array<Array<SubcategoryNumberOfQuestionsModel>>>(
      API_ENDPOINTS.QUESTIONS +
        API_ENDPOINTS.GET_NUMBER_OF_REMAINING_QUESTIONS +
        "?categories_ids=" +
        categoriesId.join(",")
    );
  }
  getRandomQuestion(
    getRandomQuestionRequestModel: GetRandomQuestionRequestModel
  ): Promise<Response<Question>> {
    return this.apiService.post<Question>(
      API_ENDPOINTS.QUESTIONS + API_ENDPOINTS.GET_RANDOM_QUESTION,
      getRandomQuestionRequestModel
    );
  }
  giveRating(
    questionId: string,
    giveRatingModel: QuestionGiveRatingRequestModel
  ): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      `${API_ENDPOINTS.QUESTIONS}/${questionId}${API_ENDPOINTS.GIVE_RATING}`,
      giveRatingModel
    );
  }
  giveAnswer(
    questionId: string,
    isCorrect: boolean
  ): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      `${API_ENDPOINTS.QUESTIONS}/${questionId}${API_ENDPOINTS.GIVE_ANSWER}`,
      { isCorrect }
    );
  }

  // answers
  getAnswerByQuestionId(questionId: string): Promise<Response<Answer>> {
    return this.apiService.get<Answer>(
      API_ENDPOINTS.ANSWERS + API_ENDPOINTS.QUESTION_ID + "/" + questionId
    );
  }
  getAnswer(id: string): Promise<Response<Answer>> {
    return this.apiService.get<Answer>(API_ENDPOINTS.ANSWERS + "/" + id);
  }

  // game
  updateGame(
    model: UpdateGameRequestModel,
    gameId: string
  ): Promise<Response<Game>> {
    return this.apiService.put<Game>(API_ENDPOINTS.GAMES + "/" + gameId, model);
  }

  // package
  getPackages(): Promise<Response<Array<Package>>> {
    return this.apiService.get<Array<Package>>(
      API_ENDPOINTS.PACKAGES + "?shownInMobile=false"
    );
  }

  // payment
  initiatePayment(
    invoiceAmount: number
  ): Promise<Response<Array<PaymentMethod>>> {
    return this.apiService.post<Array<PaymentMethod>>(
      API_ENDPOINTS.PAYMENT + API_ENDPOINTS.INITIATE_PAYMENT,
      { invoiceAmount }
    );
  }
  executePayment(
    model: ExecutePaymentRequestModel
  ): Promise<Response<PaymentURLModel>> {
    return this.apiService.post<PaymentURLModel>(
      API_ENDPOINTS.PAYMENT + API_ENDPOINTS.EXECUTE_PAYMENT,
      model
    );
  }
  getPaymentStatus(paymentId: string): Promise<Response<PaymentStatus>> {
    return this.apiService.post<PaymentStatus>(
      API_ENDPOINTS.PAYMENT + API_ENDPOINTS.STATUS_PAYMENT,
      { paymentId }
    );
  }

  // discount code
  checkIfRedeemable(
    model: DiscountCodeCheckRedeemableRequestModel
  ): Promise<Response<DiscountCodeCheckRedeemableResponseModel>> {
    return this.apiService.post<DiscountCodeCheckRedeemableResponseModel>(
      API_ENDPOINTS.DISCOUNT_CODES +
        API_ENDPOINTS.DISCOUNT_CODE_CHECK_IF_REDEEMABLE,
      model
    );
  }
  redeem(
    discountCodeId: string,
    numberOfGamesPurchased?: number
  ): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      API_ENDPOINTS.DISCOUNT_CODES +
        API_ENDPOINTS.DISCOUNT_CODE_REDEEM.replaceAll("{:id}", discountCodeId),
      { numberOfGamesPurchased }
    );
  }

  // transaction
  createTransaction(model: Transaction): Promise<Response<Message>> {
    return this.apiService.post<Message>(API_ENDPOINTS.TRANSACTIONS, model);
  }
  getTransactions(): Promise<Response<Array<Transaction>>> {
    return this.apiService.get<Array<Transaction>>(API_ENDPOINTS.TRANSACTIONS);
  }

  // qrcode
  generateQRCode(link: string): Promise<Response<QRCodeResponseModel>> {
    return this.apiService.post<QRCodeResponseModel>(
      API_ENDPOINTS.QRCODE + API_ENDPOINTS.GENERATE,
      { link }
    );
  }
}
